import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  ButtonIcon,
  ButtonText,
  FormFeedback,
  OutlineCard,
  TagInput
} from '@morressier/alembic';
import {
  FormFieldKeywords,
  ValidationKindStringArray
} from '@publishing-engine/domain/types/article';
import { FieldError, useFormContext } from 'react-hook-form';
import { Box, Flex, Text, Tooltip, useBreakpointValue } from '@chakra-ui/react';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import { FieldLabel } from 'src/components/Field';
import { get, isEqual } from 'lodash';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function Keywords({
  config,
  isMetadataExtracted,
  editKeywords
}: {
  config: FormFieldKeywords;
  isMetadataExtracted: boolean;
  editKeywords: string;
}) {
  const form = useFormContext<ISubmissionFormData>();
  const {
    params: { validations, labels },
    caseDataFields: { main: field }
  } = config;

  const { getValues, watch, setValue } = form;
  const tagInputWrapperRef = useRef<HTMLDivElement>(null);
  const didInitializeKeywords = useRef(false);

  const {
    metadata: { keywords: metadataKeywords }
  } = getValues();

  useEffect(() => {
    if (didInitializeKeywords.current) return;

    const currentKeywords = getValues('keywords') || [];
    const mappedMetadataKeywords = (metadataKeywords || []).map(key => ({
      text: key
    }));

    if (
      mappedMetadataKeywords.length > 0 &&
      currentKeywords.length === 0 &&
      !isEqual(currentKeywords, mappedMetadataKeywords)
    ) {
      setValue('keywords', mappedMetadataKeywords, { shouldValidate: true });
    }

    didInitializeKeywords.current = true;
  }, [metadataKeywords, getValues, setValue]);

  const error = get(form.formState.errors, field);
  const { submitCount } = form.formState;
  const values = watch('keywords') || [];
  const [isEditing, setIsEditing] = useState(!values || values.length === 0);

  useEffect(() => {
    const nextKeywordTexts = values.map(v => v.text);
    const currentKeywordTexts =
      (getValues(field) as string[] | undefined) || [];

    if (!isEqual(currentKeywordTexts, nextKeywordTexts)) {
      setValue(field, nextKeywordTexts, {
        shouldValidate: submitCount > 0
      });
    }
  }, [values, field, getValues, setValue, submitCount]);

  const ClearAll = () => {
    form.setValue('keywords', [], { shouldValidate: submitCount > 0 });
  };

  const getKeywordErrorMessage = () => {
    if (!error) return undefined;

    if ((error as FieldError)?.message) {
      return (error as FieldError).message?.includes('must NOT have fewer than')
        ? `A minimum of ${validations.minCountOfKeywords} keywords is required.`
        : (error as FieldError).message?.includes('must NOT have more than')
        ? `A maximum of ${validations.maxCountOfKeywords} keywords is allowed.`
        : (error as FieldError).message;
    }

    if (Array.isArray(error)) {
      const firstError = error.find(Boolean);
      if (firstError?.message?.includes('must NOT have more than')) {
        return `Each keyword must be ${validations.maxLength} characters or fewer`;
      }
      return firstError?.message;
    }

    return undefined;
  };

  const shouldShowKeywordError = submitCount > 0 && Boolean(error);
  const keywordHelperText = useBreakpointValue<
    (validations: ValidationKindStringArray) => string
  >({
    base: validations =>
      `Tap + to add keywords. (Minimum ${validations.minCountOfKeywords} required)`,
    md: validations =>
      `Press Enter to add keywords. (Minimum ${validations.minCountOfKeywords} required)`
  });
  const keywordPlaceholder = useBreakpointValue({
    base: 'Type and tap + to add',
    md: 'Type and press Enter to add'
  });

  const handleAddKeyword = () => {
    const input = tagInputWrapperRef.current?.querySelector('input');
    const value = input?.value?.trim();
    if (input && value) {
      const currentKeywords = form.getValues('keywords') || [];
      form.setValue('keywords', [...currentKeywords, { text: value }], {
        shouldValidate: submitCount > 0
      });
      input.value = '';
    }
  };

  const renderReadView = () => (
    <OutlineCard width="100%" data-cy="keywords-read-view" padding={4}>
      <Flex flexDirection="column" gap={4}>
        <Text textStyle="headingItem" data-cy="keywords-heading">
          Keywords
        </Text>
        <Flex gap={1} data-cy="keywords-list" wrap="wrap">
          {values.map(keyword => (
            <Badge
              size="context"
              key={keyword.text}
              data-cy="keyword-badge"
              whiteSpace="pre-wrap"
            >
              {keyword.text}
            </Badge>
          ))}
        </Flex>
        <ButtonText
          width="fit-content"
          variant="primary"
          onClick={() => setIsEditing(true)}
          data-cy="edit-keywords-button"
        >
          {editKeywords}
        </ButtonText>
      </Flex>
    </OutlineCard>
  );

  const renderEditView = () => (
    <>
      <OutlineCard width="100%" padding={4}>
        <Flex flexDirection="column" gap={4} data-cy="keywords-edit-view">
          <FieldLabel
            isRequired={validations.required}
            label={labels.main}
            subText={labels.subtitle}
            helpText={labels.helpText}
            data-cy="keywords-field-label"
          />
          <Box textStyle="textContext">
            <Flex gap={2} alignItems="center" ref={tagInputWrapperRef}>
              <Box flex={1}>
                <TagInput
                  placeholder={keywordPlaceholder}
                  isInvalid={shouldShowKeywordError}
                  control={form.control}
                  name="keywords"
                  data-cy="keywords-tag-input"
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    '& span': {
                      whiteSpace: 'normal !important',
                      wordBreak: 'break-word'
                    }
                  }}
                />
              </Box>
              <Box display={{ base: 'block', md: 'none' }}>
                <Tooltip
                  hasArrow
                  shouldWrapChildren
                  placement="top"
                  label="Add Keyword"
                  borderRadius={4}
                >
                  <ButtonIcon
                    size="small"
                    ariaLabel="keywords-add-button"
                    icon={<FontAwesomeIcon icon={faPlus} />}
                    data-cy="info-icon"
                    onClick={handleAddKeyword}
                  />
                </Tooltip>
              </Box>
            </Flex>
            {shouldShowKeywordError && (
              <FormFeedback variant="error">
                {getKeywordErrorMessage()}
              </FormFeedback>
            )}
            <Text data-cy="keywords-helper-text">
              {keywordHelperText?.(validations)}
            </Text>
          </Box>
        </Flex>
        <Box marginTop="4px">
          <ButtonText onClick={ClearAll}>Clear All</ButtonText>
        </Box>
      </OutlineCard>
    </>
  );

  return isMetadataExtracted && !isEditing
    ? renderReadView()
    : renderEditView();
}
